import React, { useEffect, useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import GenericTable from "../../GenericTable/GenericTable";
import userData from "../../../../data/user_list_success.json";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import config from "../../../../config";
import { AuthContext } from "../../../../authentication/AuthContext";

const userColumns = [
  { id: "username", label: "Username" },
  { id: "email_id", label: "Email" },
  { id: "phone_number", label: "Phone Number" },
  { id: "last_login", label: "Last Login" },
];

function UserManagementList() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext); // ✅ Get user from AuthContext
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Modal states
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRow) return;

    try {
      // Call backend soft delete
      const res = await fetch(`${config.API_BASE_URL}/client/user/${selectedRow.user_id}`, {
        method: "DELETE",
        headers: config.getHeaders()
      });
      const json = await res.json();

      if (!res.ok || json?.success === false) {
        console.error("Delete failed:", json?.error?.details || json?.message);
        // optional: show a snackbar here if you have one
        // setSnackbarMsg(json?.message || "Failed to delete user.");
        // setSnackbarOpen(true);
        return; // don't update UI if backend failed
      }

      // ✅ Refresh the page after successful delete
      window.location.reload();
    } catch (e) {
      console.error("Network error deleting user:", e?.message || e);
      // optional: snackbar error here too
    } finally {
      handleClose(); // close modal either way
    }
  };

  useEffect(() => {
    let abort = false;

    (async () => {
      try {
        setLoading(true);

        // ✅ Get client_id dynamically from user context or config
        const clientId = user?.client_id || config.clientId;

        console.log("[USER LIST] Fetching users with client_id:", clientId);
        console.log("[USER LIST] User from context:", user);
        console.log("[USER LIST] Config:", config);

        // ✅ Don't make API call if client_id is null/undefined
        if (!clientId) {
          console.error("[USER LIST] No client_id available - skipping API call");
          if (!abort) {
            setRows([]);
            setLoading(false);
          }
          return;
        }

        const res = await fetch(
          `${config.API_BASE_URL}/client/user/list?client_id=${clientId}&page=1&limit=50`,
          { headers: config.getHeaders() }
        );
        const json = await res.json();

        console.log("[USER LIST] API Response:", json);

        if (!res.ok || json?.success === false) {
          // fallback to empty on error; you can toast if needed
          console.error("[USER LIST] API call failed:", json?.message || json?.error);
          if (!abort) setRows([]);
          return;
        }

        const data = Array.isArray(json.data) ? json.data : [];

        // ✅ normalize fields the table expects (fallbacks)
        const normalized = data.map((u) => {
          const rawDate = u.last_login;
          let formattedDate = "N/A";

          if (rawDate) {
            try {
              const d = new Date(rawDate);
              const month = String(d.getMonth() + 1).padStart(2, "0");
              const day = String(d.getDate()).padStart(2, "0");
              const year = d.getFullYear();
              const timePart = d.toTimeString().split(" ")[0]; // HH:MM:SS
              formattedDate = `${month}-${day}-${year} ${timePart}`;
            } catch {
              formattedDate = rawDate;
            }
          }

          return {
            user_id: u.user_id,
            username: u.username || "N/A",
            email_id: u.email_id || "N/A",
            phone_number: u.phone_number || "N/A",
            last_login: formattedDate || "N/A",
            role: u.role || "user",  // ✅ Include role for conditional delete
          };
        });


        if (!abort) setRows(normalized);
      } catch (_e) {
        console.error("[USER LIST] Exception:", _e);
        if (!abort) setRows([]);
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => {
      abort = true;
    };
  }, [user]); // ✅ Re-run when user changes

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }


  return (
    <>
      <GenericTable
        title="Current Users"
        columns={userColumns}
        rows={rows}
        showView={false}
        showEdit={true}
        showDelete={true}
        onView={(row) => alert(`View user: ${row.username}`)}
        onEdit={(row) => navigate(`/user-management/${row.user_id}/edit-user`, { state: { userId: row.user_id } })}
        onDelete={handleDeleteClick}
        disableDelete={(row) => row.role === "admin"}
        customHeaderButtons={[
          {
            label: "Add New User",
            icon: "add",
            onClick: () => navigate(`/user-management/add-user`)
          }
        ]}
      />

      {/* ✅ Delete Confirmation Modal */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedRow?.username}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" color="secondary">
            No
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserManagementList;
